import PricingDiagram from "@/assets/images/config/pricing-diagram.svg";
import DefaultRatesCard from "@/assets/images/config/default-rates-card.svg";
import CustomRatesCard from "@/assets/images/config/custom-rates-card.svg";
import ContractPricingProfilesCard from "@/assets/images/config/contract-pricing-profiles-card.svg";
import { Button, Icon, Modal, Typography } from "@/components/atoms";

interface HowPricingWorksModalProps {
  open: boolean;
  onClose: () => void;
}

export const HowPricingWorksModal = ({ open, onClose }: HowPricingWorksModalProps) => {
  const featuredIcon = (
    <div className="flex">
      <div className="flex items-center justify-center p-2 rounded-full bg-warning-light">
        <div className="flex items-center justify-center p-2 rounded-full bg-warning">
          <Icon name="LampCharge" size="lg" className="flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  const renderButtons = () => (
    <Button variant="secondary" size="lg" onClick={onClose} className="mt-8">
      Close
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-large"
      title="How Pricing Works + Examples"
      description="Simple Pricing Options for Your Business"
      featuredIcon={featuredIcon}
      renderButtons={renderButtons}
      className="!max-h-[90vh] !overflow-hidden !flex !flex-col"
      headerClassName="mb-5"
    >
      <div className="overflow-y-auto -mx-6 px-6 -mt-5 pt-5 -mb-8 pb-8 max-h-[60vh]">
        <Typography variant="paragraph" className="text-neutral-dark-gray">RideMinder helps you set fair prices for your customers with a smart system that gives you as much control as you need. You can choose how simple or detailed you want your pricing to be.</Typography>
        <div className="flex items-center justify-center">
          <img className="object-contain py-4 px-2 xl:py-6 xl:px-6" src={PricingDiagram} alt="pricing-diagram" />
        </div>
        <div className="flex flex-col gap-y-3 text-neutral-dark-gray">
          <div className="flex flex-col gapx-y-1">
            <Typography variant="action">Pricing Hierarchy Explanation</Typography>
            <Typography variant="paragraph" className="mt-1">When calculating trip prices, the system follows this hierarchy:</Typography>
          </div>
          <Typography variant="paragraph">
            <ol className="list-decimal ml-6 space-y-2">
              <li>
                Contract Pricing Profile &ndash; If a company has a contract pricing profile attached, the system uses it first.
              </li>
              <li>
                Inside the Contract Pricing Profile:
                <ul className="list-disc ml-6 mt-1 space-y-1">
                  <li>It first checks if the trip falls under a Zone Rate (if set).</li>
                  <li>If no zone rate matches, it checks for a State Rate (if set).</li>
                </ul>
              </li>
              <li>
                Custom Rates &ndash; If no rates are found inside the contract pricing profile, the system checks if you&apos;ve set a custom
                rate based on state-level bookings.
              </li>
              <li>
                Default Rates &ndash; If no custom rates are found, the system falls back to the RideMinder system default rates.
              </li>
            </ol>
          </Typography>
        </div>
        <DefaultRatesDescription />
        <CustomRatesDescription />
        <ContractPricingProfilesDescription />
      </div>
    </Modal>
  );
};

const DefaultRatesDescription = () => (
  <>
    <div className="flex flex-col md:flex-row overflow-hidden rounded-lg mt-12">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Default Rates</Typography>
          <Icon isCustom name="speedtest" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="text-neutral-dark-gray mt-2">
          This is the fastest way to get started. RideMinder uses a sophisticated algorithm
          developed over decades to create fair market rates for your services.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img
          src={DefaultRatesCard}
          alt="default-rates-card"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
    <div className="flex flex-row gap-x-2.5 mt-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              The system sets professionally-calibrated prices that are fair for both you and your customers
            </li>
            <li>
              You can adjust all prices up or down by a simple percentage (like 5%)
            </li>
            <li>
              Changes apply only to the default pricing, but won&apos;t affect any custom pricing you&apos;ve set up
            </li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              You&apos;re just getting started
            </li>
            <li>
              You want a simple pricing system based on industry standards
            </li>
            <li>
              You want pricing that&apos;s already optimized for the limousine market
            </li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);

const CustomRatesDescription = () => (
  <>
    <div className="flex flex-col md:flex-row overflow-hidden rounded-lg mt-12">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Custom Rates</Typography>
          <Icon name="Setting5" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="text-neutral-dark-gray mt-2">
          RideMinder follows a clear pricing priority per state. Use Custom Rates to add and configure your own pricing for each state.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img
          src={CustomRatesCard}
          alt="custom-rates-card"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
    <div className="flex flex-row gap-x-2.5 mt-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              Select a state that you want to add custom rates to
            </li>
            <li>
              Setup different rates per kilometer for different distance ranges
            </li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              You operate in specific states with different pricing needs
            </li>
            <li>
              Your operating costs vary between states
            </li>
            <li>
              You have different competition or demand levels by state
            </li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);

const ContractPricingProfilesDescription = () => (
  <>
    <div className="flex flex-col md:flex-row overflow-hidden rounded-lg mt-12">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Contract Pricing Profiles</Typography>
          <Icon name="UserOctagon" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="text-neutral-dark-gray mt-2">
          RideMinder allows you to create contract pricing packages tailored to specific clients or companies. When calculating trip prices, the system will apply the <b>Zone Rate</b> first (if set), then the <b>State Rate</b>.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img
          src={ContractPricingProfilesCard}
          alt="contract-pricing-profiles-card"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
    <div className="flex flex-row gap-x-2.5 mt-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="action" className="mt-2">Zone Rates</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              Define specific geographic zones with a reference point and coverage radius
            </li>
            <li>
              Set special prices that apply only within these defined zones
            </li>
            <li>
              Perfect for high-demand areas like airports or business districts
            </li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="action" className="mt-2">Zone Rates</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              A client/company have special areas where costs or demand are different
            </li>
            <li>
              A client/company serve specific venues or facilities regularly
            </li>
            <li>
              A client/company want premium pricing for high-traffic locations
            </li>
          </ol>
        </Typography>
      </div>
    </div>
    <div className="flex flex-row gap-x-2.5 mt-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="action" className="mt-2">State Rates</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              Select a state that you want to add custom rates to
            </li>
            <li>
              Setup different fixed rate per range for different distance ranges
            </li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="action" className="mt-2">State Rates</Typography>
        <Typography variant="paragraph" className="space-y-2 list-decimal list-inside mt-2">
          <ol className="list-decimal list-outside pl-5 space-y-2">
            <li>
              A client/company operate in specific states with different pricing needs
            </li>
            <li>
              A client/company operating costs very between states
            </li>
            <li>
              A client/company have different competition or demand levels by state
            </li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);